/**
 * Minimalerei Reviews — Phase 3.F.2
 *
 * <minimalerei-reviews> Custom Element. Fetches reviews from Judge.me's
 * public widget endpoint and renders them as branded cards on the PDP.
 *
 * Endpoint:
 *   GET https://judge.me/api/v1/widgets/product_review
 *     ?api_token=<TOKEN>&shop_domain=<DOMAIN>&handle=<HANDLE>
 *     &per_page=<N>&page=<P>
 *
 * The element is wired up via data-* attributes set in Liquid (see
 * sections/minimalerei-reviews.liquid):
 *   data-product-handle / -id, data-shop-domain, data-api-token,
 *   data-per-page.
 *
 * On fetch failure (no token, network error, CORS): the skeleton
 * cards are removed and an error message + Judge.me-anchor link
 * surfaces instead. Stats card stays visible regardless because
 * it's server-rendered.
 */

const STAR_FULL =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
const STAR_EMPTY =
  '<svg viewBox="0 0 24 24" fill="currentColor" fill-opacity="0.25" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
const ICON_VERIFIED =
  '<svg class="mp-reviews__card-verified" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="verified"><path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z"/><path d="m9 12 2 2 4-4"/></svg>';

class MinimalereiReviews extends HTMLElement {
  constructor() {
    super();
    this.allReviews = [];
    this.dedupKeys = new Set();
    this.page = 1;
    this.totalPages = 1;
    this.nextApiPage = 1;
    this.fetchBatchSize = 3;
    this.maxApiPages = 60; // safety cap (~300 reviews max)
    this.exhausted = false;
    this.fetching = false;
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-reviews-grid]');
    this.errorEl = this.querySelector('[data-reviews-error]');
    this.paginationEl = this.querySelector('[data-pagination]');
    this.prevBtn = this.querySelector('[data-page-prev]');
    this.nextBtn = this.querySelector('[data-page-next]');
    this.indicator = this.querySelector('[data-page-indicator]');
    if (!this.grid) return;

    this.handle = this.dataset.productHandle;
    this.shopDomain = this.dataset.shopDomain;
    this.apiToken = this.dataset.apiToken;
    this.perPage = parseInt(this.dataset.perPage, 10) || 6;

    this.prevBtn?.addEventListener('click', () => this.gotoPage(this.page - 1));
    this.nextBtn?.addEventListener('click', () => this.gotoPage(this.page + 1));

    this.bindWriteTrigger();
    this.fetchAllReviews();
  }

  /**
   * The "Bewertung schreiben" button programmatically clicks Judge.me's
   * own `.jdgm-write-rev-link` from a (typically hidden) Review Widget
   * placed elsewhere on the page. That triggers Judge.me's native form
   * modal — same flow Judge.me normally uses, just from our button.
   *
   * If no jdgm-write-rev-link exists yet (Marc didn't add the widget),
   * we fall back to the old behaviour: navigate to data-write-url.
   */
  bindWriteTrigger() {
    const openBtn = this.querySelector('[data-open-modal]');
    if (!openBtn) return;

    openBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const jdgmTrigger = document.querySelector('.jdgm-write-rev-link');
      if (jdgmTrigger) {
        jdgmTrigger.click();
        return;
      }
      const url = openBtn.dataset.writeUrl;
      if (url) window.location.href = url;
    });
  }

  async fetchAllReviews() {
    if (!this.apiToken || !this.shopDomain) {
      this.showError();
      return;
    }

    // Lazy strategy: load first batch (3 pages, ~15 reviews) for
    // a fast initial render. As the visitor approaches the last
    // display-page, fetchMoreIfNeeded() pulls the next batch in
    // the background.
    const ok = await this.fetchMoreReviews({ initial: true });
    if (!ok || this.allReviews.length === 0) {
      this.showError();
      return;
    }

    this.totalPages = Math.max(1, Math.ceil(this.allReviews.length / this.perPage));
    this.page = 1;
    this.renderPage();
    this.updatePagination();
  }

  /**
   * Fetches the next batch of API pages, parses + dedups against the
   * existing pool, filters to picture-bearing reviews, re-sorts by
   * date, and updates totalPages. Returns false on hard failure
   * (no token / network), true otherwise — even when the batch
   * ends up empty (we then mark `exhausted`).
   */
  async fetchMoreReviews({ initial = false } = {}) {
    if (this.fetching || this.exhausted) return true;
    this.fetching = true;

    const pages = [];
    for (let i = 0; i < this.fetchBatchSize && this.nextApiPage <= this.maxApiPages; i++) {
      pages.push(this.nextApiPage++);
    }
    if (pages.length === 0) {
      this.exhausted = true;
      this.fetching = false;
      return true;
    }

    try {
      const responses = await Promise.all(pages.map((p) => this.fetchPage(p)));
      let added = 0;
      let distribution = null;

      responses.forEach((data) => {
        const parsed = this.parseWidgetHTML(data.widget || '');
        if (initial && !distribution) distribution = this.parseDistribution(data.widget || '');

        parsed.forEach((r) => {
          const key = `${r.reviewer.name}__${r.body.slice(0, 60)}`;
          if (this.dedupKeys.has(key)) return;
          this.dedupKeys.add(key);
          if (Array.isArray(r.pictures) && r.pictures.length > 0) {
            this.allReviews.push(r);
            added++;
          }
        });
      });

      // No new reviews this batch → Judge.me has nothing more.
      if (added === 0) this.exhausted = true;

      // Re-sort by date desc after each batch so newer reviews from
      // later API pages slot in correctly.
      this.allReviews.sort((a, b) => {
        const tA = a.created_at_iso ? Date.parse(a.created_at_iso) : 0;
        const tB = b.created_at_iso ? Date.parse(b.created_at_iso) : 0;
        return tB - tA;
      });

      if (distribution) this.updateDistributionDOM(distribution);

      console.log(
        `[minimalerei-reviews] batch ${pages[0]}-${pages.at(-1)}: +${added}, total ${this.allReviews.length}, exhausted=${this.exhausted}`,
      );
      return true;
    } catch (err) {
      console.error('[minimalerei-reviews] fetch failed:', err);
      return false;
    } finally {
      this.fetching = false;
    }
  }

  async fetchPage(page) {
    const url = new URL('https://judge.me/api/v1/widgets/product_review');
    url.searchParams.set('api_token', this.apiToken);
    url.searchParams.set('shop_domain', this.shopDomain);
    url.searchParams.set('handle', this.handle);
    url.searchParams.set('page', page);
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  parseDistribution(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const rows = doc.querySelectorAll('.jdgm-histogram-row, .jdgm-histogram__row, [data-rating][data-frequency]');
    const dist = {};
    rows.forEach((row) => {
      const rating = parseInt(row.getAttribute('data-rating'), 10);
      const freq = parseInt(row.getAttribute('data-frequency'), 10);
      if (rating >= 1 && rating <= 5 && !isNaN(freq)) dist[rating] = freq;
    });
    return Object.keys(dist).length ? dist : null;
  }

  updateDistributionDOM(dist) {
    const total = Object.values(dist).reduce((a, b) => a + b, 0);
    if (total === 0) return;

    // Liquid renders rows top-down 5→1, so nth-child(1) is 5★, nth(5) is 1★.
    const rows = this.querySelectorAll('.mp-reviews__dist-row');
    rows.forEach((row, i) => {
      const star = 5 - i;
      const count = dist[star] || 0;
      const pct = (count / total) * 100;
      const fill = row.querySelector('.mp-reviews__dist-fill');
      const countEl = row.querySelector('.mp-reviews__dist-count');
      if (fill) fill.style.width = `${pct}%`;
      if (countEl) countEl.textContent = count;
    });
  }

  parseWidgetHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const reviewEls = doc.querySelectorAll('.jdgm-rev');
    console.log('[minimalerei-reviews] parsed', reviewEls.length, 'review elements from widget');

    return Array.from(reviewEls).map((el, i) => {
      const ratingAttr = el.getAttribute('data-rating') || el.querySelector('[data-score]')?.getAttribute('data-score');
      const rating = parseInt(ratingAttr, 10) || 5;

      const name = el.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Anonym';
      const body = el.querySelector('.jdgm-rev__body')?.textContent.trim() || '';
      const timestampEl = el.querySelector('.jdgm-rev__timestamp');
      const timestamp = timestampEl?.textContent.trim() || '';
      // Judge.me stores the canonical ISO date on the timestamp's
      // data-content attr (or sometimes <time datetime="…">). We need
      // it for client-side sorting; the visible label text alone
      // varies by locale and "vor X Tagen" wording.
      const isoCreatedAt =
        timestampEl?.getAttribute('data-content') ||
        el.querySelector('time')?.getAttribute('datetime') ||
        el.getAttribute('data-created-at') ||
        '';
      const variant = el.querySelector('.jdgm-rev__product-variant')?.textContent.trim() || '';
      const verified = !!el.querySelector('.jdgm-rev__icon-verified-buyer, .jdgm--verified-buyer');

      // Pictures: prefer the high-res URL on the <a> wrapper (Judge.me
      // links thumbs to full-size images), fall back to img.src or
      // data-src. Then upgrade Judge.me CDN URLs from `_thumb`/`_compact`
      // to `_huge` for sharper rendering when only the thumb exists.
      const pictures = [];
      const seenUrls = new Set();

      const upgradeUrl = (url) =>
        url
          .replace(/_thumb(?=\.[a-z]+)/i, '_huge')
          .replace(/_compact(?=\.[a-z]+)/i, '_huge')
          .replace(/_small(?=\.[a-z]+)/i, '_huge');

      const addPic = (raw) => {
        if (!raw || raw.includes('data:image')) return;
        const url = upgradeUrl(raw);
        if (seenUrls.has(url)) return;
        seenUrls.add(url);
        pictures.push({ urls: { huge: url } });
      };

      el.querySelectorAll('a[href]').forEach((a) => {
        const href = a.getAttribute('href');
        if (href && /\.(jpg|jpeg|png|webp|gif)/i.test(href)) addPic(href);
      });
      el.querySelectorAll('img').forEach((img) => {
        addPic(img.getAttribute('data-src') || img.src);
      });
      el.querySelectorAll('[style*="background-image"]').forEach((bgEl) => {
        const m = bgEl.getAttribute('style').match(/url\(['"]?([^'")]+)/);
        if (m) addPic(m[1]);
      });

      return {
        rating,
        body,
        reviewer: { name },
        created_at_label: timestamp,
        created_at_iso: isoCreatedAt,
        product_variant_title: variant,
        verified,
        pictures,
      };
    });
  }

  renderPage() {
    const start = (this.page - 1) * this.perPage;
    const slice = this.allReviews.slice(start, start + this.perPage);
    this.renderReviews(slice, true);
  }

  async gotoPage(n) {
    if (n < 1) return;

    // Lazy-load up front: if the requested page would be empty or
    // partial because we haven't fetched enough yet, pull more
    // batches first so the page renders complete on the first paint.
    while (n > this.totalPages && !this.exhausted) {
      const before = this.allReviews.length;
      await this.fetchMoreReviews();
      this.totalPages = Math.max(1, Math.ceil(this.allReviews.length / this.perPage));
      if (this.allReviews.length === before) break; // no progress
    }

    // Pre-warm one batch ahead when on the last page, so the next
    // click is also instant.
    if (!this.exhausted && n >= this.totalPages) {
      const before = this.allReviews.length;
      await this.fetchMoreReviews();
      this.totalPages = Math.max(1, Math.ceil(this.allReviews.length / this.perPage));
      if (this.allReviews.length === before) {
        // already correct
      }
    }

    if (n > this.totalPages) n = this.totalPages;
    this.page = n;
    this.renderPage();
    this.updatePagination();
    this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  renderReviews(reviews, replace) {
    if (replace) this.grid.innerHTML = '';

    if (reviews.length === 0 && replace) {
      this.showError();
      return;
    }

    const frag = document.createDocumentFragment();
    reviews.forEach((review) => frag.appendChild(this.buildCard(review)));
    this.grid.appendChild(frag);
  }

  buildCard(review) {
    const li = document.createElement('li');
    li.className = 'mp-reviews__card';

    // The grid only includes reviews with user-uploaded pictures
    // (filtered upstream in fetchAllReviews), so a photo URL is
    // guaranteed to exist here.
    const picture = (review.pictures || [])[0];
    const photoUrl = picture?.urls?.huge || picture?.urls?.original || picture?.url;
    const variant = review.product_variant_title;

    if (photoUrl) {
      const photo = document.createElement('div');
      photo.className = 'mp-reviews__card-photo';
      const img = document.createElement('img');
      img.src = photoUrl;
      img.loading = 'lazy';
      img.alt = `Bewertung von ${review.reviewer?.name || ''}`.trim();
      photo.appendChild(img);
      if (variant) {
        const pill = document.createElement('span');
        pill.className = 'mp-reviews__card-pill';
        pill.textContent = variant;
        photo.appendChild(pill);
      }
      li.appendChild(photo);
    }

    const body = document.createElement('div');
    body.className = 'mp-reviews__card-body';

    // Stars
    const stars = document.createElement('span');
    stars.className = 'mp-reviews__card-stars';
    stars.setAttribute('aria-label', `${review.rating} von 5 Sternen`);
    for (let i = 1; i <= 5; i++) {
      stars.insertAdjacentHTML('beforeend', i <= review.rating ? STAR_FULL : STAR_EMPTY);
    }
    body.appendChild(stars);

    // Quote — strip surrounding quote marks if Judge.me added them
    const quote = document.createElement('p');
    quote.className = 'mp-reviews__card-quote';
    quote.textContent = `„${(review.body || '').trim()}"`;
    body.appendChild(quote);

    // Author row
    const author = document.createElement('div');
    author.className = 'mp-reviews__card-author';

    const name = review.reviewer?.name || 'Anonym';
    const initial = name.charAt(0).toUpperCase();

    const avatar = document.createElement('span');
    avatar.className = 'mp-reviews__card-avatar';
    avatar.textContent = initial;
    author.appendChild(avatar);

    const meta = document.createElement('span');
    meta.className = 'mp-reviews__card-meta';

    const nameEl = document.createElement('span');
    nameEl.className = 'mp-reviews__card-name';
    nameEl.textContent = name;
    if (review.verified === 'buyer' || review.verified === true) {
      nameEl.insertAdjacentHTML('beforeend', ICON_VERIFIED);
    }
    meta.appendChild(nameEl);

    if (review.created_at_label) {
      const dateEl = document.createElement('span');
      dateEl.className = 'mp-reviews__card-date';
      dateEl.textContent = review.created_at_label;
      meta.appendChild(dateEl);
    }

    author.appendChild(meta);
    body.appendChild(author);
    li.appendChild(body);

    return li;
  }

  updatePagination() {
    if (!this.paginationEl) return;
    // Hide the pagination only when there's truly nothing more to
    // browse — i.e. one page total AND no further API pages exist.
    this.paginationEl.hidden = this.totalPages <= 1 && this.exhausted;
    if (this.indicator) {
      const suffix = this.exhausted ? '' : '+';
      this.indicator.textContent = `${this.page} / ${this.totalPages}${suffix}`;
    }
    if (this.prevBtn) this.prevBtn.disabled = this.page === 1;
    // Next stays clickable as long as more might be fetched lazily.
    if (this.nextBtn) this.nextBtn.disabled = this.page >= this.totalPages && this.exhausted;
  }

  showError() {
    if (this.grid) this.grid.innerHTML = '';
    if (this.errorEl) this.errorEl.hidden = false;
    if (this.paginationEl) this.paginationEl.hidden = true;
  }
}

if (!customElements.get('minimalerei-reviews')) {
  customElements.define('minimalerei-reviews', MinimalereiReviews);
}
